/**
 * The WRITE half of the offline flag-decision cache, plus the withdrawal that
 * retires a replay once the provider answers again.
 *
 * Extracted from `OpenFeatureContextSync`, which mounts it: that component is a
 * context-sync component and the cache lifecycle is its own concern, with its
 * own suite. The recovery (READ half) is mounted here too so the whole
 * lifecycle has one home.
 */
import { OpenFeature, ProviderEvents, ProviderStatus } from '@openfeature/react-sdk';
import { useEffect, useRef } from 'react';

import { useNavigatorOnline } from '@/hooks/desktop/useNavigatorOnline';
import { isTauri } from '@/tauri/utils';
import { FEATURE_FLAG_DOMAIN } from '@heidi/runtime/feature-flags';
import {
  useClearOfflineCachedFlagReplay,
  useOfflineCachedFlagDecisionsRecovery,
} from './offline-cached-flag-decisions';
import { OFFLINE_CACHED_FLAG_KEYS } from './offline-cached-flag-keys';
import {
  OFFLINE_RECORDING_FLAG_KEY,
  persistCachedFlagDecision,
  persistOfflineRecordingFlagDecision,
} from './offline-recording-flag-cache';

export function useOfflineFlagCacheSync({
  userRefId,
  isDesktopOnlineReady,
}: {
  userRefId: string | undefined;
  isDesktopOnlineReady: boolean;
}): void {
  // The READ side, mounted once for the whole app so the keychain identity is
  // recovered a single time rather than once per flag consumer.
  useOfflineCachedFlagDecisionsRecovery(userRefId);
  const clearOfflineCachedFlagReplay = useClearOfflineCachedFlagReplay();

  // A ConfigurationChanged observed while the probe still reports offline —
  // holds the userRefId it belongs to, or null. Declared OUTSIDE the effect so
  // it survives the effect's teardown/re-run when `isDesktopOnlineReady` flips.
  // See the withdrawal comment below for the narrow case this actually earns
  // its keep on: a re-identify racing the same gap.
  const pendingConfigurationChangedUserRef = useRef<string | null>(null);
  // Reactive, not a one-time read inside the effect: without this, a browser
  // online/offline transition that isn't paired with an `isDesktopOnlineReady`
  // or `userRefId` change would leave a remembered event unretired until some
  // unrelated re-render happened to re-run the effect.
  const browserIsOnline = useNavigatorOnline();

  useEffect(() => {
    const client = OpenFeature.getClient(FEATURE_FLAG_DOMAIN);

    // `setContext` (the identify effect in `OpenFeatureContextSync`) is fire-and-forget, so the
    // provider can report READY on the PREVIOUS context while the
    // offline-recording flag is still its pre-identify default. Persisting
    // then would overwrite a last-good `true` cache with a stale `false`.
    // Drive the cache write off the provider's settle events instead, and only
    // once the applied context has actually settled to the current user —
    // reading the freshly-evaluated value from the client, never the stale
    // render value. See [[Default async-hydrated UI state to 'unknown']].
    const persistIfSettled = (source: 'mount' | 'ready' | 'configuration-changed') => {
      // The context guard is shared by both halves: a provider answering for
      // somebody else's targeting key is not answering for us, whatever its
      // status says.
      if (!userRefId || OpenFeature.getContext().targetingKey !== userRefId) {
        return;
      }

      // ---- THE WITHDRAWAL ----
      //
      // Three pieces of current evidence can retire the replay:
      //
      // 1. A READY event while the app is online. The Flagsmith adapter drops
      //    whether an event came from its SDK cache, so offline READY is
      //    ambiguous and cannot retire a bounded replay.
      //
      // 2. A fresh ConfigurationChanged event while the app is online. It
      //    doesn't need its own READY check: see the fourth case below for
      //    why a ConfigurationChanged always implies status just reached
      //    READY too.
      //
      // 3. READY observed at mount while the app is online. READY is latched,
      //    so without current reachability it is only history and must not
      //    withdraw a replay during an offline remount.
      //
      // A fourth case is NOT current evidence but earns the same withdrawal:
      // a ConfigurationChanged that arrived WHILE THE PROBE STILL SAID
      // OFFLINE (case 2 above, but with `isAppOnline` false) is dropped in the
      // moment — it cannot retire anything the probe itself contradicts — but
      // it is not nothing either: it proves the provider answered at least
      // once during the outage (the Flagsmith adapter's `onChange` always
      // fires a `Ready` immediately before a `ConfigurationChanged` that
      // carries `flagsChanged`, so the SDK's own status tracking has already
      // promoted to READY by this point — the poll case needs no memory of
      // its own). Remembered here, and withdrawn the moment
      // `isDesktopOnlineReady` catches up.
      //
      // Ordinarily that withdrawal would also happen for free: when the probe
      // catches up the effect re-runs, `persistIfSettled('mount')` fires, and
      // case 3 above sees `providerReachedReady` already true from the Ready
      // that preceded this event — status is sticky, so it stays READY until
      // something explicitly moves it. This memory earns its keep only in the
      // gap where something does: a re-identify (`OpenFeatureContextSync`'s
      // identify effect, e.g. a trait change or a different user) racing in
      // the same window can leave the client at RECONCILING or ERROR by the
      // time the probe reports online, and without this the mount-time check
      // would then find `providerReachedReady` false and never withdraw —
      // stranding a kill switch that landed mid-outage cached ON for the rest
      // of the session. The seven-day persist below stays gated behind READY
      // regardless — this only widens what can WITHDRAW a replay, never what
      // can seed one.
      const providerReachedReady = client.providerStatus === ProviderStatus.READY;
      const isAppOnline = isDesktopOnlineReady && browserIsOnline;
      const freshProviderAnswer =
        (source === 'ready' && isAppOnline) ||
        (source === 'configuration-changed' && isAppOnline) ||
        (source === 'mount' && providerReachedReady && isAppOnline);
      const remembersOfflineAnswerNowReachable =
        pendingConfigurationChangedUserRef.current === userRefId && isAppOnline;

      if (freshProviderAnswer || remembersOfflineAnswerNowReachable) {
        clearOfflineCachedFlagReplay();
        pendingConfigurationChangedUserRef.current = null;
      } else if (source === 'configuration-changed' && !isAppOnline) {
        pendingConfigurationChangedUserRef.current = userRefId;
      }

      // ---- THE WRITE, which keeps every guard ----
      //
      // A value cached for seven days has to come off a READY provider on a
      // settled context with the app online. Nothing weaker earns that.
      if (!isAppOnline || !providerReachedReady) return;

      persistOfflineRecordingFlagDecision(
        userRefId,
        client.getBooleanValue(OFFLINE_RECORDING_FLAG_KEY, false)
      );

      // Same settle, same guards, same seven-day cache — widened from the one
      // offline-recording key to the allowlist, so each value below is one
      // Flagsmith answered rather than a default standing in for one.
      if (isTauri()) {
        for (const flagKey of OFFLINE_CACHED_FLAG_KEYS) {
          persistCachedFlagDecision(
            userRefId,
            flagKey,
            client.getBooleanValue(flagKey, false)
          );
        }
      }
    };

    let registeringReadyHandler = true;
    const onProviderReady = () => {
      // The OpenFeature SDK invokes a READY handler synchronously when it is
      // registered against an already-READY client. That is a status snapshot,
      // not a fresh provider answer, so let the guarded mount check handle it.
      if (registeringReadyHandler) return;
      persistIfSettled('ready');
    };
    const onConfigurationChanged = () => {
      persistIfSettled('configuration-changed');
    };

    client.addHandler(ProviderEvents.Ready, onProviderReady);
    registeringReadyHandler = false;
    client.addHandler(ProviderEvents.ConfigurationChanged, onConfigurationChanged);
    // A READY status is historical, so mount-time withdrawal also requires
    // current app reachability. Fresh events above carry their own evidence.
    persistIfSettled('mount');

    return () => {
      client.removeHandler(ProviderEvents.Ready, onProviderReady);
      client.removeHandler(ProviderEvents.ConfigurationChanged, onConfigurationChanged);
    };
  }, [isDesktopOnlineReady, browserIsOnline, userRefId, clearOfflineCachedFlagReplay]);
}
